import { Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuCalendar, LuMail } from "react-icons/lu";

import { getSuppressionAudienceSyncedContacts } from "../api/suppression-audience";
import { useLoading, useToaster } from "../hooks";
import {
  SuppressionAudience,
  SuppressionAudienceSyncedContact,
} from "../types/suppression-audience";
import DataTable, { DataTableColumn } from "./data-table";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";

const columns: DataTableColumn<SuppressionAudienceSyncedContact>[] = [
  {
    name: "Email",
    icon: <LuMail />,
    width: 280,
    cell: ({ value }) => (
      <Text truncate title={value.email}>
        {value.email}
      </Text>
    ),
  },
  {
    name: "Synced At",
    icon: <LuCalendar />,
    width: 160,
    cell: ({ value }) => (
      <Text>{new Date(value["synced-at"]).toLocaleString()}</Text>
    ),
  },
];

interface SuppressionAudienceSyncedContactsDialogProps {
  audience: SuppressionAudience | null;
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  workspaceSlug: string;
}

export function SuppressionAudienceSyncedContactsDialog({
  audience,
  isOpen,
  onOpenChange,
  workspaceSlug,
}: SuppressionAudienceSyncedContactsDialogProps) {
  const [contacts, setContacts] = React.useState<
    SuppressionAudienceSyncedContact[]
  >([]);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  React.useEffect(() => {
    if (!isOpen || !audience) return;
    withLoading(async () => {
      setContacts([]);
      setHasLoaded(false);
      await withToaster(async () => {
        const data = await getSuppressionAudienceSyncedContacts(
          workspaceSlug,
          audience.id,
        );
        setContacts(data);
        setHasLoaded(true);
      });
    });
  }, [audience, isOpen, withLoading, withToaster, workspaceSlug]);

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="xl">
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            Synced Contacts — {audience?.name ?? ""}{" "}
            <Text as="span" fontSize="sm" fontWeight="normal" color="fg.muted">
              (last 24 hours)
            </Text>
          </DialogTitle>
        </DialogHeader>
        <DialogBody pb="6">
          {isLoading && !hasLoaded ? (
            <DataTable data={[]} columns={columns} skeleton={3} />
          ) : hasLoaded && contacts.length === 0 ? (
            <Stack alignItems="center" py="8">
              <Text color="fg.muted" fontSize="sm">
                No synced contacts in the last 24 hours.
              </Text>
            </Stack>
          ) : (
            <DataTable
              data={contacts}
              columns={columns}
              skeleton={isLoading ? 3 : undefined}
            />
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
